import { Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput, MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { map } from 'rxjs';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { ContactPoint, ContactPointCategory } from '../../../api/app-types/contact-point.app';
import { Gender } from '../../../api/app-types/gender.app';
import { ClarificationValue } from '../../../api/app-types/relative.app';
import { CreateSponsorPerson, SponsorPerson, UpdateSponsorPerson } from '../../../api/app-types/sponsor.app';
import { SPONSOR_SERVICE } from '../../../api/services/sponsor.service';
import { Address, CreateAddressBody } from '../../../api/app-types/address.app';
import { AddressCompletion } from '../../../core/components/address-completion/address-completion';
import { LvrContactPoint } from '../../../core/components/contact-point/contact-point';
import { LvrDataProtection } from '../../../core/components/data-protection/data-protection';
import { LvrDefaultActions } from '../../../core/components/default-actions/default.actions';
import { LvrField } from '../../../core/components/field/field';
import { LvrButton } from '../../../core/components/lvr-button/lvr-button';
import { LvrInputDirective } from '../../../core/directives/lvr-input.directive';
import { hasError } from '../../../shared/helpers/has-error.func';
import { wrapPromiseWithNotify } from '../../../shared/helpers/notify.func';
import { NOTIFICATION_SERVICE } from '../../../shared/notification/notification';
import { LvrPaddingDirective } from '../../../shared/padding/padding.directive';
import { LvrAppendAsteriskPipe } from '../../../shared/pipes/append-asterisk.pipe';
import { ImmediateErrorStateMatcher } from '../../../shared/validators/immediate-error-state-matcher';
import { maxStringLength } from '../../../shared/validators/length.validator';
import { SessionManagerService } from '../../../shared/storage/session-manager.service';
import { hasPermission } from '../../../shared/permissions/permission.func';
import { isContactPointComplete } from '../../../shared/helpers/is-contact-point-complete.func';

@Component({
	selector: 'lvr-sponsor-person-create',
	templateUrl: './sponsor-person-create.html',
	styleUrl: './sponsor-person-create.scss',
	imports: [
		TranslocoPipe,
		LvrPaddingDirective,
		LvrAppendAsteriskPipe,
		ReactiveFormsModule,
		LvrInputDirective,
		LvrField,
		MatSelectModule,
		MatFormFieldModule,
		MatInput,
		MatInputModule,
		MatTooltip,
		AddressCompletion,
		LvrContactPoint,
		MatIcon,
		MatButtonModule,
		LvrDataProtection,
		LvrDefaultActions,
		LvrButton,
	],
})
export class SponsorPersonCreate {
	private readonly fb = inject(FormBuilder);
	private readonly env = inject(ENVIRONMENT);
	private readonly titleRegex = new RegExp(this.env.titleRegexStr);
	private readonly router = inject(Router);
	private readonly api = inject(SPONSOR_SERVICE);
	private readonly ntfy = inject(NOTIFICATION_SERVICE);
	private readonly transloco = inject(TranslocoService);
	private readonly session = inject(SessionManagerService);
	private readonly route = inject(ActivatedRoute);
	private readonly data = toSignal(this.route.data, { initialValue: this.route.snapshot.data });
	private readonly sponsorId = toSignal(this.route.paramMap.pipe(map((pm) => Number(pm.get('id')))));
	private readonly personId = toSignal(this.route.paramMap.pipe(map((pm) => Number(pm.get('personId')))));
	public readonly person = computed(() => this.data()?.['person'] as SponsorPerson | undefined);
	public readonly isEditMode = computed(() => !!this.person());
	public readonly pageTitleKey = computed(() =>
		this.isEditMode() ? 'sponsor.persons.edit.title' : 'sponsor.person_create.title',
	);

	public readonly contactPointCategories = computed(
		() => (this.data()?.['contactPointCategories']?.entities ?? []) as ContactPointCategory[],
	);
	public readonly genders = computed(() => (this.data()?.['genders'] ?? []) as Gender[]);
	public readonly saving = signal(false);
	public readonly form = this.initForm(this.fb);
	public readonly canCreateSponsorPersonsPermission = hasPermission(
		this.session.permissions,
		'InstitutionPersons.Create',
	);
	public readonly canEditSponsorPersonsPermission = hasPermission(this.session.permissions, 'InstitutionPersons.Edit');
	public readonly canSubmitSponsorsPermission = computed(() =>
		this.isEditMode() ? this.canEditSponsorPersonsPermission() : this.canCreateSponsorPersonsPermission(),
	);
	public readonly remarkCtrlMaxLength = this.env.textareaMaxLength;
	public readonly titleCtrlMaxLength = this.env.maxTitleLength;
	public readonly nameCtrlMaxLength = this.env.maxNameLength;
	public readonly roleCtrlMaxLength = this.env.textInputMaxLength;
	public readonly hasError = hasError;
	public readonly matcher = new ImmediateErrorStateMatcher();

	public constructor() {
		effect(() => {
			const p = this.person();
			if (!p) return;
			this.form.patchValue({
				remark: p.remark ?? '',
				title: p.title ?? '',
				genderId: p.gender?.id ?? undefined,
				firstName: p.firstName ?? '',
				lastName: p.name ?? '',
				role: p.role ?? '',
				address: p.address,
				communicationWays: p.contactPoints ?? [],
				clarification: {
					hasBeenClarified: p.hasBeenClarified ?? false,
					clarifiedAt: p.clarifiedAt ?? undefined,
					typeOfClarification: p.typeOfClarification ?? undefined,
				},
			});
		});
	}

	private initForm(fb: FormBuilder) {
		return fb.group({
			remark: ['', [maxStringLength(this.env.textareaMaxLength)]],
			title: ['', [Validators.pattern(this.titleRegex), maxStringLength(this.env.maxTitleLength)]],
			genderId: this.fb.control<number | undefined>(undefined),
			firstName: ['', [Validators.pattern(this.env.nameRegexStr), maxStringLength(this.env.maxNameLength)]],
			lastName: [
				'',
				[Validators.pattern(this.env.nameRegexStr), maxStringLength(this.env.maxNameLength), Validators.required],
			],
			role: ['', [maxStringLength(this.env.textInputMaxLength)]],
			address: this.fb.control<CreateAddressBody | Address | undefined>(undefined),
			communicationWays: this.fb.control<ContactPoint[]>([]),
			clarification: this.fb.control<ClarificationValue>({
				hasBeenClarified: false,
				clarifiedAt: undefined,
				typeOfClarification: undefined,
			}),
		});
	}

	public get communicationWays(): ContactPoint[] {
		return (this.form.controls.communicationWays.value ?? []) as ContactPoint[];
	}

	public updateValues(data: { valid: boolean; value: ContactPoint }, index: number) {
		const ctrl = this.form.get('communicationWays');
		if (!ctrl) return;
		const values = (ctrl.value ?? []) as ContactPoint[];
		if (index >= 0 && index < values.length) {
			Object.assign(values[index], data.value);
		} else {
			values.push(data.value);
		}
	}

	public cancel() {
		if (this.isEditMode()) {
			this.router.navigate(['/desk/sponsor', this.sponsorId(), 'persons', this.personId()]);
			return;
		}
		this.router.navigate(['/desk/sponsor', this.sponsorId(), 'persons']);
	}

	public async save() {
		if (this.saving()) return;

		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}

		const EMPTY_ADDRESS: CreateAddressBody = {
			streetAndHouseNumber: '',
			postalCodeAndCity: '',
			soundexStreet: '',
			anonym: '',
			identifier: '',
		};

		this.saving.set(true);

		try {
			const value = this.form.value;
			const clar = value.clarification ?? {
				hasBeenClarified: false,
				clarifiedAt: undefined,
				typeOfClarification: undefined,
			};

			const body: CreateSponsorPerson = {
				sponsorId: this.sponsorId()!,
				address: value.address ?? EMPTY_ADDRESS,
				communicationWays: (value.communicationWays ?? []).filter(isContactPointComplete),
				firstName: value.firstName ?? '',
				lastName: value.lastName ?? '',
				title: value.title ?? '',
				genderId: value.genderId ?? undefined,
				role: value.role ?? '',
				remark: value.remark ?? '',
				hasBeenClarified: clar.hasBeenClarified,
				clarifiedAt: clar.clarifiedAt,
				typeOfClarification: clar.typeOfClarification,
			};

			if (this.isEditMode()) {
				const updateBody: UpdateSponsorPerson = {
					...body,
					id: this.personId()!,
				};
				await wrapPromiseWithNotify(
					this.api.updatePerson(updateBody),
					this.transloco.translate('base.notification_message.sponsor_person_edited', {
						firstName: body.firstName,
						name: body.lastName,
					}),
					this.ntfy,
					this.transloco.translate('base.notification_message.sponsor_person_edit_error'),
				);
				await this.router.navigate(['/desk/sponsor', this.sponsorId(), 'persons', this.personId()]);
			} else {
				await wrapPromiseWithNotify(
					this.api.createPerson(body),
					this.transloco.translate('base.notification_message.sponsor_person_created'),
					this.ntfy,
					this.transloco.translate('base.notification_message.sponsor_person_create_error'),
				);
				await this.router.navigate(['/desk/sponsor', this.sponsorId(), 'persons']);
			}
		} finally {
			this.saving.set(false);
		}
	}

	public addCommunicationWay() {
		const vals = this.form.get('communicationWays')?.value ?? [];
		vals.push({
			id: -1,
			personId: -1,
			personTypeId: -1,
			categoryContactPointId: -1,
			contactValue: '',
			preferredWayOfContact: false,
			remark: '',
		});
		this.form.get('communicationWays')?.setValue(vals);
	}

	public removeCommunicationWay(index: number) {
		const ctrl = this.form.get('communicationWays');
		if (!ctrl) return;
		const values = ctrl.value ?? [];
		if (index !== -1) {
			values.splice(index, 1);
			ctrl.setValue(values);
		}
	}
}
